/* Tiny particle sim for the receipt bin: an air-blower lottery machine.
   Positions are px inside the bin box (0,0 = top-left of the bin, y grows down).
   Everything scales with the bin height so it behaves the same at any size. */

const rnd = (min, max) => min + Math.random() * (max - min);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const PAPER_NEAR = [255, 253, 247];
const PAPER_FAR = [214, 206, 188];

/* Far-away slips are a touch darker so the pile has depth */
const paperFor = (z) => {
  const c = PAPER_FAR.map((f, i) => Math.round(f + (PAPER_NEAR[i] - f) * z));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};

/* Mound shape: tallest in the middle, `u` picks the particle's layer in it */
export const restY = (p, w, h, slipH) => {
  const nx = (p.x - w / 2) / (w * 0.56);
  const mound = h * (0.14 + 0.46 * Math.pow(Math.max(0, 1 - nx * nx), 0.6));
  return h - slipH * 0.35 - p.u * mound;
};

export const createParticles = (n, w, h, { poured = false, slipH = 60 } = {}) =>
  Array.from({ length: n }, (_, id) => {
    const z = Math.random();
    const p = {
      id,
      z,
      scale: 0.8 + 0.32 * z,
      paper: paperFor(z),
      u: Math.pow(Math.random(), 0.75),
      x: rnd(0.05, 0.95) * w,
      y: -rnd(0.15, 1.3) * h, // start above the bin and pour in
      vx: rnd(-0.1, 0.1) * h,
      vy: rnd(0, 0.4) * h,
      a: rnd(-180, 180),
      av: rnd(-120, 120),
      flip: rnd(0, 360),
      f1: rnd(1.1, 2.4),
      f2: rnd(1.8, 3.6),
      p1: rnd(0, Math.PI * 2),
      p2: rnd(0, Math.PI * 2),
      dir: Math.random() < 0.5 ? -1 : 1,
      resting: false,
    };
    if (poured) {
      p.y = restY(p, w, h, slipH);
      p.vx = p.vy = p.av = 0;
      p.flip = 0;
      p.resting = true;
    }
    return p;
  });

/* Advance one particle by dt seconds. `air` is 0..1 (blower strength). */
export const stepParticle = (p, dt, t, air, w, h, slipW, slipH) => {
  const g = 3.4 * h;
  const cx = w / 2;
  const nx = (p.x - cx) / (w / 2); // -1 left wall … 1 right wall
  const depth = p.y / h; // 0 = bin rim, 1 = floor, negative = above the bin
  let ax = 0;
  let ay = g;

  if (air > 0) {
    /* Jet from the floor: strongest low down and in the middle */
    const jet = 2.35 + 1.9 * Math.exp(-nx * nx * 3.5);
    ay -= air * g * jet * Math.pow(clamp(depth, 0, 1.2), 1.15);
    /* Rolling circulation: up the middle, out over the rim, back in along the floor */
    if (depth < 0.4) ax += air * h * 5 * (0.4 - depth) * (nx >= 0 ? 1 : -1);
    else ax -= air * h * 3.4 * nx * (depth - 0.4);
    /* Turbulence — smooth, per-particle, so paths stay continuous. Fades out
       above the rim so tossed slips arc back down instead of floating. */
    const churn = air * clamp(0.25 + depth * 1.5, 0, 1);
    ax += churn * h * 5.6 * (Math.sin(t * p.f1 + p.p1) + 0.6 * Math.sin(t * p.f2 * 1.3 + p.p2 + p.y * 0.012));
    ay += churn * h * 4.6 * (Math.sin(t * p.f2 + p.p2) + 0.5 * Math.sin(t * p.f1 * 1.7 + p.p1 + p.x * 0.012));
    /* Occasional gust from the nozzle throws a slip high above the rim */
    if (depth > 0.55 && Math.abs(nx) < 0.55 && Math.random() < dt * 0.9 * air) {
      p.vy -= h * rnd(2.4, 3.6);
      p.vx += h * rnd(-0.8, 0.8);
    }
    p.resting = false;
  } else if (!p.resting) {
    /* Falling paper: swings side to side like a leaf */
    ax += h * 2.2 * Math.sin(t * p.f1 * 2.4 + p.p1);
  }

  if (p.resting) return;

  const damp = Math.exp(-(air > 0 ? 2.2 : 2.9) * dt); // paper has a lot of drag
  p.vx = (p.vx + ax * dt) * damp;
  p.vy = (p.vy + ay * dt) * damp;
  p.x += p.vx * dt;
  p.y += p.vy * dt;

  /* Walls (invisible above the rim too, so nothing escapes sideways) */
  const m = slipW * 0.45;
  if (p.x < m) {
    p.x = m;
    p.vx = Math.abs(p.vx) * 0.35;
  } else if (p.x > w - m) {
    p.x = w - m;
    p.vx = -Math.abs(p.vx) * 0.35;
  }
  /* Ceiling: top of the stage */
  if (p.y < -1.15 * h) {
    p.y = -1.15 * h;
    p.vy = Math.abs(p.vy) * 0.2;
  }

  /* Mound / floor */
  const floor = restY(p, w, h, slipH);
  const onFloor = p.y >= floor;
  if (onFloor) {
    p.y = floor;
    if (p.vy > 0) p.vy *= -0.12;
    p.vx *= 0.82;
  }

  /* Spin: driven by sideways motion + gusts, heavy damping */
  const gust = air * 760 * Math.sin(t * p.f2 * 1.6 + p.p1);
  p.av += (gust + p.vx * 1.4 * p.dir - p.av * 1.6) * Math.min(1, dt * 3);
  p.a += p.av * dt;

  /* Flip (rotateX): paper turning over in the air, flat again once it lands */
  const speed = Math.hypot(p.vx, p.vy);
  if (onFloor && air === 0) {
    const flat = Math.round(p.flip / 180) * 180;
    p.flip += (flat - p.flip) * Math.min(1, dt * 9);
    p.av *= Math.exp(-9 * dt);
    if (Math.abs(p.vy) < 0.05 * h && Math.abs(p.vx) < 0.05 * h && Math.abs(flat - p.flip) < 2) {
      p.flip = flat;
      p.vx = p.vy = p.av = 0;
      p.resting = true;
    }
  } else {
    p.flip += p.dir * (140 + speed * 0.9) * dt;
  }
};

export const transformFor = (p) =>
  `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0) translate(-50%, -50%) rotate(${p.a.toFixed(1)}deg) rotateX(${p.flip.toFixed(1)}deg) scale(${p.scale.toFixed(3)})`;
